import React, { useState } from "react";
import {
  Modal,
  Paper,
  Box,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SimpleCustomTable from "../../../components/table/SimpleCustomTable";
import { SimpleColumn, SimpleRow, RowId } from "../../../components/table/interface";
import ModalDeletedRecordDetail from "./ModalDeletedRecordDetail";

export interface DeletedLendSummary {
  backup_id: number;
  original_lend_id: number;
  value: number;
  total_value: number;
  insurance_charge: number;
  deleted_by_name: string;
  deleted_at: string;
}

interface ModalDeletedLendsListProps {
  open: boolean;
  onClose: () => void;
  clientName: string | null;
  lends: DeletedLendSummary[];
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
}

const formatCurrency = (value: unknown) =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 })
    .format(Number(value || 0));

const formatDateTime = (value?: string): string => {
  if (!value) return "";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString("es-CO");
};

const ModalDeletedLendsList: React.FC<ModalDeletedLendsListProps> = ({
  open, onClose, clientName, lends, showAlert,
}) => {
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBackupId, setDetailBackupId] = useState<number | null>(null);

  const rows: SimpleRow[] = lends.map((l, i) => ({
    id: `lend-${l.backup_id}-${i}` as RowId,
    backup_id: l.backup_id,
    original_lend_id: l.original_lend_id,
    value: l.value,
    total_value: l.total_value,
    insurance_charge: l.insurance_charge,
    deleted_by_name: l.deleted_by_name,
    deleted_at: l.deleted_at,
  }));

  const columns: SimpleColumn[] = [
    { field: "original_lend_id", headerName: "ID Préstamo", minWidth: 100, align: "center" },
    {
      field: "value", headerName: "Valor", minWidth: 100, align: "right",
      renderCell: ({ row }: { row: SimpleRow }) => formatCurrency(row.value),
    },
    {
      field: "total_value", headerName: "Valor Total", minWidth: 100, align: "right",
      renderCell: ({ row }: { row: SimpleRow }) => formatCurrency(row.total_value),
    },
    {
      field: "insurance_charge", headerName: "Seguro", minWidth: 90, align: "right",
      renderCell: ({ row }: { row: SimpleRow }) => formatCurrency(row.insurance_charge),
    },
    { field: "deleted_by_name", headerName: "Borrado por", minWidth: 130, wrapText: true },
    {
      field: "deleted_at", headerName: "Fecha y hora", minWidth: 140, align: "center",
      renderCell: ({ row }: { row: SimpleRow }) => formatDateTime(String(row.deleted_at ?? "")),
    },
    {
      field: "actions", headerName: "", minWidth: 90, align: "center",
      renderCell: ({ row }: { row: SimpleRow }) => (
        <Button
          size="small"
          startIcon={<VisibilityIcon fontSize="small" />}
          onClick={() => {
            setDetailBackupId(Number(row.backup_id));
            setDetailOpen(true);
          }}
        >
          Ver
        </Button>
      ),
    },
  ];

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Paper
          sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: "95vw", maxWidth: 900, maxHeight: "85vh",
            display: "flex", flexDirection: "column", borderRadius: 2, overflow: "hidden",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, bgcolor: "primary.main", color: "#fff" }}>
            <Typography variant="h6">{`Préstamos borrados${clientName ? ` — ${clientName}` : ""}`}</Typography>
            <IconButton onClick={onClose} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 2, overflow: "auto" }}>
            <SimpleCustomTable
              rows={rows}
              columns={columns}
              maxHeight={500}
              showBorders
              showCount
              wrapCellContent
            />
          </Box>
        </Paper>
      </Modal>

      <ModalDeletedRecordDetail
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        recordType="lend"
        backupId={detailBackupId}
        showAlert={showAlert}
      />
    </>
  );
};

export default ModalDeletedLendsList;
